import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  MessageCircle,
  X,
  ChevronsRight,
  ChevronsLeft,
  ArrowLeft,
  Send,
  Loader2,
  BookOpen,
  Trash2,
  Sparkles,
  EyeOff,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { sanitizeRichTextHtml } from "@/lib/sanitizeHtml";

// ── types ──────────────────────────────────────────────────────────────────
type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  citations?: string[];
};

type Mode = "hidden" | "collapsed" | "chat" | "article";

interface Position {
  x: number;
  y: number;
}

const DRAG_CLICK_THRESHOLD_PX = 5;
const VIEWPORT_PADDING_PX = 8;
const BUBBLE_SIZE_PX = 48; // h-12 w-12 closed bubble

interface KbArticleLite {
  id: number;
  slug: string;
  title: string;
  content: string | null;
  excerpt: string | null;
  updatedAt: string | null;
}

// ── localStorage persistence ───────────────────────────────────────────────
function conversationKey(userId: string | undefined, role: string): string {
  return `aiChat:conv:${userId ?? "anon"}:${role}`;
}

function loadConversation(userId: string | undefined, role: string): ChatMessage[] {
  try {
    const raw = localStorage.getItem(conversationKey(userId, role));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {}
  return [];
}

function saveConversation(userId: string | undefined, role: string, msgs: ChatMessage[]) {
  try {
    localStorage.setItem(conversationKey(userId, role), JSON.stringify(msgs));
  } catch {}
}

const POSITION_STORAGE_KEY = "aiChat:position";
const HIDDEN_STORAGE_KEY = "aiChat:hidden";

function loadPosition(): Position | null {
  try {
    const raw = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === "number" && typeof parsed?.y === "number") return parsed;
  } catch {}
  return null;
}

function savePosition(p: Position | null) {
  try {
    if (p === null) localStorage.removeItem(POSITION_STORAGE_KEY);
    else localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(p));
  } catch {}
}

function loadHidden(): boolean {
  try { return localStorage.getItem(HIDDEN_STORAGE_KEY) === "1"; } catch { return false; }
}

function saveHidden(hidden: boolean) {
  try {
    if (hidden) localStorage.setItem(HIDDEN_STORAGE_KEY, "1");
    else localStorage.removeItem(HIDDEN_STORAGE_KEY);
  } catch {}
}

function clampPosition(p: Position, width: number, height: number): Position {
  const maxX = Math.max(VIEWPORT_PADDING_PX, window.innerWidth - width - VIEWPORT_PADDING_PX);
  const maxY = Math.max(VIEWPORT_PADDING_PX, window.innerHeight - height - VIEWPORT_PADDING_PX);
  return {
    x: Math.min(Math.max(VIEWPORT_PADDING_PX, p.x), maxX),
    y: Math.min(Math.max(VIEWPORT_PADDING_PX, p.y), maxY),
  };
}

// ── SSE parser ─────────────────────────────────────────────────────────────
// Consumes a fetch ReadableStream and dispatches per-event callbacks.
async function consumeSseStream(
  body: ReadableStream<Uint8Array>,
  handlers: {
    onToken: (chunk: string) => void;
    onCitations: (slugs: string[]) => void;
    onError: (msg: string) => void;
    onDone: () => void;
  }
) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sepIndex: number;
    while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);
      const lines = rawEvent.split("\n");
      let event = "message";
      let data = "";
      for (const line of lines) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trim();
      }
      if (!data) continue;
      let parsed: any;
      try { parsed = JSON.parse(data); } catch { continue; }

      if (event === "token" && typeof parsed?.chunk === "string") handlers.onToken(parsed.chunk);
      else if (event === "citations" && Array.isArray(parsed?.slugs)) handlers.onCitations(parsed.slugs);
      else if (event === "error") handlers.onError(parsed?.message ?? "Chat failed");
      else if (event === "done") handlers.onDone();
    }
  }
}

// ── sub-components ─────────────────────────────────────────────────────────

function CitationCard({
  slug,
  onOpen,
}: {
  slug: string;
  onOpen: (slug: string) => void;
}) {
  const { data } = useQuery<KbArticleLite>({
    queryKey: ["/api/kb/articles", slug],
    queryFn: async () => {
      const res = await fetch(`/api/kb/articles/${slug}`, { credentials: "include" });
      if (!res.ok) throw new Error("not found");
      return res.json();
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <button
      type="button"
      onClick={() => onOpen(slug)}
      className="flex w-full items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
    >
      <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate">{data?.title ?? slug}</span>
      <span className="text-xs text-muted-foreground shrink-0">Open ↗</span>
    </button>
  );
}

function MessageBubble({
  message,
  onOpenArticle,
}: {
  message: ChatMessage;
  onOpenArticle: (slug: string) => void;
}) {
  const isUser = message.role === "user";
  // Strip the [[article:slug]] tokens — citations render as separate cards below.
  const cleaned = message.content.replace(/\[\[article:[a-z0-9-]+\]\]/g, "");

  return (
    <div className={cn("flex flex-col gap-1.5", isUser ? "items-end" : "items-start")}>
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-3.5 py-2 text-sm",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground"
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleaned}</ReactMarkdown>
          </div>
        )}
      </div>
      {message.citations && message.citations.length > 0 && (
        <div className="flex w-full max-w-[85%] flex-col gap-1.5">
          {message.citations.map((slug) => (
            <CitationCard key={slug} slug={slug} onOpen={onOpenArticle} />
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleView({ slug, onBack }: { slug: string; onBack: () => void }) {
  const { data, isLoading, isError } = useQuery<KbArticleLite>({
    queryKey: ["/api/kb/articles", slug],
    queryFn: async () => {
      const res = await fetch(`/api/kb/articles/${slug}`, { credentials: "include" });
      if (!res.ok) throw new Error("not found");
      return res.json();
    },
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to chat
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {isError && <p className="text-sm text-destructive">Couldn't load this article.</p>}
        {data && (
          <article>
            <h2 className="text-xl font-semibold mb-2">{data.title}</h2>
            {data.updatedAt && (
              <p className="text-xs text-muted-foreground mb-4">
                Updated {new Date(data.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            )}
            <div
              className="prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(data.content ?? "") }}
            />
          </article>
        )}
      </div>
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────

export default function AiChatPopup() {
  const { user, isAuthenticated } = useAuth();
  const { currentRole } = useRole();
  const userId: string | undefined = (user as any)?.id;

  const [mode, setMode] = useState<Mode>(() => (loadHidden() ? "hidden" : "collapsed"));
  const [articleSlug, setArticleSlug] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [input, setInput] = useState("");
  const [position, setPosition] = useState<Position | null>(() => loadPosition());
  const [dragging, setDragging] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    offset: Position;          // pointer offset from element top-left at drag start
    startPointer: Position;    // pointer screen position at drag start (for click detection)
    moved: boolean;            // exceeded the click threshold
  } | null>(null);

  // Hydrate from localStorage when role/user becomes available
  useEffect(() => {
    if (!isAuthenticated || !currentRole) return;
    setMessages(loadConversation(userId, currentRole));
    setArticleSlug(null);
    setMode((m) => (m === "article" ? "chat" : m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentRole, isAuthenticated]);

  // Persist on changes
  useEffect(() => {
    if (!isAuthenticated || !currentRole) return;
    saveConversation(userId, currentRole, messages);
  }, [messages, userId, currentRole, isAuthenticated]);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  // Cleanup on unmount: abort any in-flight stream
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  // Persist hidden state when changed
  useEffect(() => {
    saveHidden(mode === "hidden");
  }, [mode]);

  // Re-clamp position when viewport shrinks so the widget never escapes the screen
  useEffect(() => {
    function onResize() {
      if (!position || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const clamped = clampPosition(position, rect.width, rect.height);
      if (clamped.x !== position.x || clamped.y !== position.y) {
        setPosition(clamped);
        savePosition(clamped);
      }
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [position]);

  // ── drag ────────────────────────────────────────────────────────────────
  const handleDragPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    // Ignore drags that originate on a button — those are clicks, not drag handles.
    const target = e.target as HTMLElement;
    if (target.closest("button") && e.currentTarget !== target.closest("button")) return;
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    dragStateRef.current = {
      offset: { x: e.clientX - rect.left, y: e.clientY - rect.top },
      startPointer: { x: e.clientX, y: e.clientY },
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  }, []);

  const handleDragPointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const drag = dragStateRef.current;
    if (!drag || !containerRef.current) return;
    const dx = Math.abs(e.clientX - drag.startPointer.x);
    const dy = Math.abs(e.clientY - drag.startPointer.y);
    if (!drag.moved && dx + dy < DRAG_CLICK_THRESHOLD_PX) return; // still a potential click
    drag.moved = true;
    const rect = containerRef.current.getBoundingClientRect();
    const elementTopLeft = clampPosition(
      { x: e.clientX - drag.offset.x, y: e.clientY - drag.offset.y },
      rect.width,
      rect.height
    );
    // `position` stores the BUBBLE's top-left so the panel can anchor its
    // bottom-right to the bubble's bottom-right (and grow up-left). When the
    // user drags the open panel, translate the panel's top-left into the
    // equivalent bubble top-left so the anchor stays at the panel's BR corner.
    const isPanel = rect.width > BUBBLE_SIZE_PX;
    const bubbleTopLeft = isPanel
      ? {
          x: elementTopLeft.x + rect.width - BUBBLE_SIZE_PX,
          y: elementTopLeft.y + rect.height - BUBBLE_SIZE_PX,
        }
      : elementTopLeft;
    setPosition(bubbleTopLeft);
  }, []);

  const handleDragPointerUp = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const drag = dragStateRef.current;
    dragStateRef.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (drag?.moved && position) savePosition(position);
    return drag?.moved ?? false; // caller decides whether to treat as click
  }, [position]);

  const { data: suggestionsRes } = useQuery<{ suggestions: string[] }>({
    queryKey: ["/api/ai-agent/suggestions"],
    queryFn: async () => {
      const res = await fetch("/api/ai-agent/suggestions", { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      return res.json();
    },
    enabled: isAuthenticated && mode !== "collapsed",
    staleTime: 60 * 60 * 1000,
  });
  const suggestions = suggestionsRes?.suggestions ?? [];

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || streaming) return;

    const userMsg: ChatMessage = { role: "user", content: text.trim() };
    const nextMessages: ChatMessage[] = [...messages, userMsg];
    setMessages(nextMessages);
    setInput("");
    setStreamingContent("");
    setStreaming(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let assembled = "";
    let citations: string[] = [];

    try {
      const res = await fetch("/api/ai-agent/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        signal: controller.signal,
        body: JSON.stringify({ messages: nextMessages }),
      });
      if (!res.ok || !res.body) {
        // A non-OK response here means the server rejected the request before
        // streaming started (e.g. the DEV-298 rate limiter on
        // POST /api/ai-agent/chat returns 429 + { message } — see
        // server/routes/aiAgentRoutes.ts's chatLimiter). Surface that message
        // instead of the generic status code so the user sees "you're sending
        // messages too quickly" rather than "Chat failed (429)".
        let message = `Chat failed (${res.status})`;
        try {
          const body = await res.json();
          if (typeof body?.message === "string") message = body.message;
        } catch {
          // body wasn't JSON (or already consumed) — keep the generic message
        }
        throw new Error(message);
      }
      await consumeSseStream(res.body, {
        onToken: (chunk) => {
          assembled += chunk;
          setStreamingContent(assembled);
        },
        onCitations: (slugs) => { citations = slugs; },
        onError: (msg) => { throw new Error(msg); },
        onDone: () => {},
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: assembled, citations },
      ]);
      setStreamingContent("");
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `Sorry — I ran into an error: ${err?.message ?? "unknown"}`,
          },
        ]);
      }
      setStreamingContent("");
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [messages, streaming]);

  const clearConversation = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setStreamingContent("");
    setStreaming(false);
  }, []);

  const handleOpenArticle = useCallback((slug: string) => {
    setArticleSlug(slug);
    setMode("article");
  }, []);

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    if (input.trim()) sendMessage(input);
  }, [input, sendMessage]);

  if (!isAuthenticated) return null;

  // `position` stores the bubble's top-left in pixels. The bubble renders at
  // that point; the panel anchors its bottom-right corner to the bubble's
  // bottom-right corner so it always grows up-left from there.
  const bubblePositionStyle: React.CSSProperties = position
    ? { left: `${position.x}px`, top: `${position.y}px` }
    : { right: "1rem", bottom: "1rem" };
  const panelPositionStyle: React.CSSProperties = position
    ? {
        right: `${Math.max(0, window.innerWidth - position.x - BUBBLE_SIZE_PX)}px`,
        bottom: `${Math.max(0, window.innerHeight - position.y - BUBBLE_SIZE_PX)}px`,
      }
    : { right: "1rem", bottom: "1rem" };

  // When hidden, show a slim edge tab on the right so the user can bring it back.
  if (mode === "hidden") {
    return (
      <button
        type="button"
        onClick={() => setMode("chat")}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[70] flex flex-col items-center gap-1 rounded-l-md bg-primary text-primary-foreground px-1.5 py-3 shadow-lg hover:px-2 transition-all"
        aria-label="Show chat assistant"
        title="Show assistant"
      >
        <Sparkles className="h-3.5 w-3.5" />
      </button>
    );
  }

  const isOpen = mode !== "collapsed";

  return (
    <>
      {!isOpen && (
        <button
          ref={containerRef as unknown as React.RefObject<HTMLButtonElement>}
          type="button"
          onPointerDown={handleDragPointerDown}
          onPointerMove={handleDragPointerMove}
          onPointerUp={(e) => {
            const wasDragged = handleDragPointerUp(e);
            if (!wasDragged) setMode("chat");
          }}
          style={bubblePositionStyle}
          className={cn(
            "fixed z-[70] flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform",
            dragging ? "cursor-grabbing scale-105" : "cursor-grab hover:scale-105"
          )}
          aria-label="Open chat assistant"
        >
          <MessageCircle className="h-5 w-5 pointer-events-none" />
        </button>
      )}

      {isOpen && (
        <div
          ref={containerRef}
          style={panelPositionStyle}
          className={cn(
            "fixed z-[70] flex flex-col rounded-xl border border-border bg-background shadow-2xl",
            // Skip CSS transitions while dragging so the position tracks the pointer 1:1.
            !dragging && "transition-[width,height]",
            mode === "article"
              ? "w-[min(800px,calc(100vw-2rem))] h-[min(700px,calc(100vh-2rem))]"
              : "w-[min(400px,calc(100vw-2rem))] h-[min(600px,calc(100vh-2rem))]"
          )}
        >
          <div
            onPointerDown={handleDragPointerDown}
            onPointerMove={handleDragPointerMove}
            onPointerUp={handleDragPointerUp}
            className={cn(
              "flex items-center gap-2 border-b border-border px-3 py-2 select-none",
              dragging ? "cursor-grabbing" : "cursor-grab"
            )}
          >
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium flex-1">Assistant</span>
            {mode === "chat" && messages.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearConversation} title="Clear conversation">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setMode(mode === "chat" ? "article" : "chat"); }}
              disabled={mode === "article" && !articleSlug}
              title={mode === "chat" ? "Expand" : "Collapse to chat"}
            >
              {mode === "chat" ? <ChevronsLeft className="h-3.5 w-3.5" /> : <ChevronsRight className="h-3.5 w-3.5" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setMode("hidden")} title="Hide widget">
              <EyeOff className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setMode("collapsed")} title="Minimize">
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          {mode === "article" && articleSlug ? (
            <ArticleView slug={articleSlug} onBack={() => setMode("chat")} />
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                {messages.length === 0 && !streaming && (
                  <EmptyState suggestions={suggestions} onPick={sendMessage} />
                )}
                {messages.map((m, idx) => (
                  <MessageBubble key={idx} message={m} onOpenArticle={handleOpenArticle} />
                ))}
                {streaming && (
                  <MessageBubble
                    message={{ role: "assistant", content: streamingContent || "…" }}
                    onOpenArticle={handleOpenArticle}
                  />
                )}
                <div ref={messagesEndRef} />
              </div>

              <form
                onSubmit={handleSubmit}
                className="border-t border-border p-2 flex items-end gap-2"
              >
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={streaming ? "Thinking…" : "Ask me anything…"}
                  rows={1}
                  className="resize-none min-h-9 max-h-32 text-sm"
                  disabled={streaming}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                <Button type="submit" size="sm" disabled={streaming || !input.trim()}>
                  {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}

function EmptyState({
  suggestions,
  onPick,
}: {
  suggestions: string[];
  onPick: (s: string) => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-6 px-2 gap-3">
      <Sparkles className="h-8 w-8 text-muted-foreground" />
      <div>
        <p className="font-medium">How can I help?</p>
        <p className="text-xs text-muted-foreground">Ask anything about the platform.</p>
      </div>
      {suggestions.length > 0 && (
        <div className="w-full flex flex-col gap-1.5 mt-2">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onPick(s)}
              className="text-left rounded-md border border-border bg-muted/30 px-3 py-2 text-xs hover:bg-muted transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
